import React from "react";
import { ThemeProvider, createTheme } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import { ToastContainer, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./toast.css";

import Routes from "./routes";
import { ConfirmProvider } from "./components/ConfirmDialog";

// Design tokens — see blueprint §6. Dark grey = main text/icon colour,
// lime = accent (~10% of the screen, reserved for the primary CTA & counters).
const theme = createTheme({
  scrollbarStyles: {
    "&::-webkit-scrollbar": { width: 6, height: 6 },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: 3,
      backgroundColor: "#cdd2d8",
    },
  },
  // Tokens neutros frios (refactor §2) — fonte única; nada de bege.
  app: {
    lime: "#CEFF00",
    limeHover: "#bce600",
    dark: "#2C353D",
    sidebarBg: "#FFFFFF",
    chatBg: "#F6F7F9",
    bubbleSent: "#E8FFB3",
    border: "#E6E9ED",
    borderInput: "#D8DBDF",
    textSecondary: "#5B6470",
    textTertiary: "#929AA4",
    chip: "#EEF0F3",
    selectedRow: "#FAFBFC",
    // refactor §2/§5: tokens adicionais (status, dados técnicos, badges)
    ink: "#1B222A",
    lineSoft: "#EEF0F3",
    hover: "#FAFBFC",
    // tipografia/acento do redesign: título em Space Grotesk, corpo em Poppins,
    // verde de acento p/ estados ativos (pills/abas/seleção) — legível no branco.
    heading: '"Space Grotesk", sans-serif',
    green: "#86C240",
    ok: "#1E9E62",
    warn: "#C77A12",
    danger: "#C0453B",
    info: "#2F6FB0",
    alt: "#6A52C4",
    mono: 'ui-monospace,SFMono-Regular,"SF Mono",Menlo,Consolas,monospace',
  },
  palette: {
    primary: { main: "#2C353D", contrastText: "#FFFFFF" },
    secondary: { main: "#CEFF00", contrastText: "#2C353D" },
    background: { default: "#F6F7F9" },
    text: { primary: "#1B222A", secondary: "#5B6470" },
  },
  typography: {
    fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  overrides: {
    MuiButton: {
      root: { textTransform: "none", fontWeight: 500 },
      outlinedSecondary: {
        color: "#2C353D",
        borderColor: "#D8DBDF",
        "&:hover": { borderColor: "#D8DBDF", backgroundColor: "#F1F3F5" },
      },
      textSecondary: { color: "#5B6470" },
      // regra do guia: CTA primário = charcoal (verde nunca é fill de botão)
      containedSecondary: {
        backgroundColor: "#2C353D",
        color: "#FFFFFF",
        "&:hover": { backgroundColor: "#1f262c" },
      },
    },
    // raio de input = --r-md (8px), igual ao guia §5.9 (busca/forms compactos)
    MuiOutlinedInput: {
      root: { borderRadius: 8 },
    },
    // MODAL padrão do sistema em TODO <Dialog> (mesmo do menuStyles.dialogPaper):
    // cantos 16px, sombra difusa e campos na escala do painel do nó (13px) — o MUI
    // põe 16px em input/label/checkbox e destoava. Vale também p/ modais que não
    // passam PaperProps.
    MuiDialog: {
      paper: {
        borderRadius: 16,
        border: "1px solid #E6E9ED",
        boxShadow: "0 24px 60px rgba(16,20,26,.22)",
        "& .MuiInputBase-input": { fontSize: 13, letterSpacing: 0 },
        "& .MuiInputLabel-root": { fontSize: 13, letterSpacing: 0 },
        "& .MuiFormHelperText-root": { fontSize: 11.5 },
        "& .MuiFormControlLabel-label": { fontSize: 13 },
        "& .MuiSelect-select:focus": { backgroundColor: "transparent" },
      },
    },
  },
  props: {
    // dropdown de <TextField select>/<Select>: abre EMBAIXO do campo (o padrão do
    // MUI v4 abre por cima, cobrindo o campo). Quem passa MenuProps próprio
    // (ex.: selectPaper do menuStyles) substitui isto.
    MuiSelect: {
      MenuProps: {
        getContentAnchorEl: null,
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        transformOrigin: { vertical: "top", horizontal: "left" },
        PaperProps: { className: "nb-select-paper" },
      },
    },
  },
});

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ConfirmProvider>
        <Routes />
      </ConfirmProvider>
      {/* posição inferior-direita (desktop) / inferior (mobile), entra por cima
          com slide+fade; TODAS auto-fecham em 5s (inclusive erros).
          pauseOnFocusLoss desligado: por padrão o react-toastify PAUSA o timer
          quando a janela perde o foco (DevTools aberto, outra janela por cima),
          e a toast ficava parada até voltar o foco — parecia que não fechava. */}
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        pauseOnFocusLoss={false}
        newestOnTop
        hideProgressBar
        transition={Slide}
      />
    </ThemeProvider>
  );
};

export default App;
